import {
  BarChart3, Bell, BookOpen, Building2, CalendarDays, Check, CheckCircle2, ChevronDown, ChevronsLeft, ChevronsRight, CircleCheck, CircleHelp, ExternalLink, FolderKanban,
  Clock3, FileCog, Handshake, LayoutDashboard, LayoutList, LoaderCircle, LogOut, Megaphone, Menu, MessageCircle, Network, Settings as SettingsIcon, X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "./i18n";
import {
  AccountWorkspace, AdditionalPermission, api, AttendanceStatus, Department, logout, markPasswordChanged, Notification, PlatformSession, platformLogout, ProductModule, Project,
  ResearchNote, ResearchNoteDraftRequest, Session, storedPlatformSession, storedSession, Task, TenantSettings, User,
} from "./api";
import { initials, koTime } from "./lib/format";
import { isPlatformLocation, parseWorkspaceRoute, routeForView, workspaceRouteUrl, type WorkspaceRoute } from "./lib/routes";
import { Avatar, Loading } from "./components/ui";
import { loadingLayoutForRoute } from "./components/Loading";
import { BrandLogo } from "./components/BrandLogo";
import { SupportInquiryModal } from "./components/SupportInquiryModal";
import type { SettingsSection, TaskSeed, View } from "./lib/types";
import { Login, PasswordChange, PlatformLogin } from "./screens/Auth";
import { Invitation } from "./screens/Invitation";
import { PlatformConsole } from "./screens/Platform";
import { CompletedTasksPage, TasksPage } from "./screens/Tasks";
import { DashboardPage } from "./screens/Dashboard";
import { CalendarPage } from "./screens/Calendar";
import { OrganizationChartPage } from "./screens/OrganizationChart";
import { CompanyRulesPage } from "./screens/CompanyRules";
import { WorkGuidesPage } from "./screens/WorkGuides";
import { BoardsPage } from "./screens/Boards";
import { StatusPage } from "./screens/Status";
import { TaskDetail } from "./screens/TaskDetail";
import { canConvertTaskToNote, taskNeedsApproval } from "./lib/org";
import { TaskCompose } from "./screens/TaskCompose";
import { ProjectBudgetPage, ProjectCreate, ProjectDetail, ProjectsPage } from "./screens/Projects";
import { NoteDetail, NotesPage, ResearchNoteCreateModal } from "./screens/Notes";
import { PerformancePage } from "./screens/Performance";
import { EvaluationPage } from "./screens/Evaluation";
import { NoticeManagementPage, SettingsPage, TaskFormsPage } from "./screens/Settings";
import { PersonalSettingsPage } from "./screens/NotificationSettings";
import { CollaborationPage } from "./screens/Collaboration";
import { AttendanceManagementPage } from "./screens/AttendanceManagement";
import { LeaveManagementPage, LeavePage, MyWorkPage } from "./screens/People";
import { applyPreferences, type UserPreferences } from "./lib/preferences";
import { currentWebPushSubscription, resolveWebPushPublicKey, subscribeCurrentBrowser } from "./lib/webPush";
import { PublicSite } from "./screens/PublicSite";
import { isPublicSiteLocation, publicSiteUrl, workspaceHandoffUrl } from "./lib/workspace";

export { workspaceDomain } from "./lib/workspace";
export { ProjectMemberPicker } from "./screens/Projects";

type Modal = { kind: "task"; seed?: TaskSeed; task?: Task } | { kind: "project" } | { kind: "note"; task?: Task } | null;

const nav: { id: View; labelKey: string; icon: typeof LayoutList; groupKey: string; module?: ProductModule; ownerOnly?: boolean; managerOnly?: boolean; permission?: AdditionalPermission; nonOwnerOnly?: boolean }[] = [
  { id: "dashboard", labelKey: "nav.home", icon: LayoutDashboard, groupKey: "nav.dashboardGroup" },
  { id: "calendar", labelKey: "nav.calendar", icon: CalendarDays, groupKey: "nav.dashboardGroup" },
  { id: "boards", labelKey: "nav.boards", icon: MessageCircle, groupKey: "nav.dashboardGroup", module: "BOARD" },
  { id: "status", labelKey: "nav.status", icon: BarChart3, groupKey: "nav.dashboardGroup", module: "STATUS" },
  { id: "tasks", labelKey: "nav.tasks", icon: LayoutList, groupKey: "nav.execution" },
  { id: "collaboration", labelKey: "nav.collaboration", icon: Handshake, groupKey: "nav.execution" },
  { id: "work-guides", labelKey: "nav.workGuides", icon: BookOpen, groupKey: "nav.execution" },
  { id: "projects", labelKey: "nav.projects", icon: FolderKanban, groupKey: "nav.execution", module: "PROJECTS" },
  { id: "performance", labelKey: "nav.performance", icon: BarChart3, groupKey: "nav.records", module: "PERFORMANCE" },
  { id: "notes", labelKey: "nav.notes", icon: BookOpen, groupKey: "nav.records", module: "RESEARCH_NOTES" },
  { id: "my-work", labelKey: "nav.myWork", icon: Clock3, groupKey: "nav.people", module: "ATTENDANCE" },
  { id: "org-chart", labelKey: "nav.orgChart", icon: Network, groupKey: "nav.people" },
  { id: "leave", labelKey: "nav.leaveRequest", icon: CalendarDays, groupKey: "nav.people", module: "ATTENDANCE" },
  { id: "task-forms", labelKey: "nav.taskForms", icon: FileCog, groupKey: "nav.management", managerOnly: true },
  { id: "evaluation", labelKey: "nav.evaluation", icon: CircleCheck, groupKey: "nav.management", module: "PERFORMANCE", managerOnly: true },
  { id: "account-management", labelKey: "nav.accounts", icon: Building2, groupKey: "nav.management", permission: "USER_ACCOUNT_MANAGE", nonOwnerOnly: true },
  { id: "announcement-management", labelKey: "nav.boardManagement", icon: Megaphone, groupKey: "nav.management", module: "BOARD", permission: "BOARD_MANAGE" },
  { id: "company-rule-management", labelKey: "nav.companyRuleManagement", icon: BookOpen, groupKey: "nav.management", permission: "COMPANY_RULE_MANAGE" },
  { id: "settings", labelKey: "nav.settings", icon: SettingsIcon, groupKey: "nav.organization", ownerOnly: true },
  { id: "attendance-management", labelKey: "nav.attendance", icon: Clock3, groupKey: "nav.management", module: "ATTENDANCE", permission: "ATTENDANCE_MANAGE" },
  { id: "leave-management", labelKey: "nav.leaveManagement", icon: CalendarDays, groupKey: "nav.management", module: "ATTENDANCE", permission: "ATTENDANCE_MANAGE" },
  { id: "work-guide-management", labelKey: "nav.workGuideManagement", icon: FileCog, groupKey: "nav.organization", permission: "WORK_GUIDE_MANAGE" },
];

export function App() {
  const [invitation, setInvitation] = useState(() => window.location.pathname === "/invitation");
  const [session, setSession] = useState<Session | null>(() => storedSession());
  const [platform, setPlatform] = useState(() => isPlatformLocation());
  const [publicSite, setPublicSite] = useState(() => isPublicSiteLocation());
  const [platformSession, setPlatformSession] = useState<PlatformSession | null>(() => storedPlatformSession());
  useEffect(() => {
    const platformUnauthorized = () => setPlatformSession(null);
    const unauthorized = () => setSession(null);
    const sessionChanged = () => setSession(storedSession());
    window.addEventListener("angelflow:platform-unauthorized", platformUnauthorized);
    window.addEventListener("angelflow:unauthorized", unauthorized);
    window.addEventListener("angelflow:session-changed", sessionChanged);
    window.addEventListener("storage", sessionChanged);
    return () => {
      window.removeEventListener("angelflow:platform-unauthorized", platformUnauthorized);
      window.removeEventListener("angelflow:unauthorized", unauthorized);
      window.removeEventListener("angelflow:session-changed", sessionChanged);
      window.removeEventListener("storage", sessionChanged);
    };
  }, []);
  useEffect(() => {
    const syncLocation = () => { setPlatform(isPlatformLocation()); setPublicSite(isPublicSiteLocation()); setInvitation(window.location.pathname === "/invitation"); };
    window.addEventListener("popstate", syncLocation);
    window.addEventListener("hashchange", syncLocation);
    return () => {
      window.removeEventListener("popstate", syncLocation);
      window.removeEventListener("hashchange", syncLocation);
    };
  }, []);
  useEffect(() => {
    if (window.location.hash !== "#platform") return;
    window.history.replaceState(null, "", `/platform${window.location.search}`);
  }, [platform]);
  if (platform) return platformSession
    ? <PlatformConsole onLogout={() => { platformLogout(); setPlatformSession(null); }} />
    : <PlatformLogin onLogin={setPlatformSession} onWorkspace={() => { window.history.pushState(null, "", publicSite ? publicSiteUrl("/login") : `/${window.location.search}`); setPlatform(false); }} />;
  if (invitation) return <Invitation onAccepted={(next) => { setSession(next); setInvitation(false); setPublicSite(false); }} />;
  if (publicSite) return <PublicSite />;
  if (!session) return <Login onLogin={setSession} />;
  if (session.passwordChangeRequired) return <PasswordChange session={session} onChanged={(token) => setSession(markPasswordChanged(session, token))} onLogout={() => { logout(); setSession(null); }} />;
  return <Workspace session={session} onSessionChanged={setSession} onLogout={() => {
    logout();
    if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1" || window.location.hostname === "::1") {
      window.history.pushState(null, "", publicSiteUrl("/login"));
      setSession(null); setPublicSite(true);
    } else {
      window.location.assign(publicSiteUrl("/login"));
    }
  }} />;
}

function Workspace({ session, onSessionChanged, onLogout }: { session: Session; onSessionChanged: (session: Session) => void; onLogout: () => void }) {
  const { t } = useTranslation();
  const [route, setRoute] = useState<WorkspaceRoute>(() => parseWorkspaceRoute());
  const [mobileNav, setMobileNav] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [modal, setModal] = useState<Modal>(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [scopedTasks, setScopedTasks] = useState<Task[]>([]);
  const [completedNextCursor, setCompletedNextCursor] = useState<string | null>(null);
  const [taskProjectFilter, setTaskProjectFilter] = useState<string | undefined>();
  const [projects, setProjects] = useState<Project[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [notes, setNotes] = useState<ResearchNote[]>([]);
  const [draftRequests, setDraftRequests] = useState<ResearchNoteDraftRequest[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const [attendance, setAttendance] = useState<AttendanceStatus | null>(null);
  const [additionalPermissions, setAdditionalPermissions] = useState<AdditionalPermission[]>([]);
  const [personalPreferences, setPersonalPreferences] = useState<UserPreferences>();
  const [loading, setLoading] = useState(true);
  const loadedWorkspace = useRef(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const applyVisibleTasks = useCallback((active: Task[], completed: Task[]) => {
    const visible = [...active, ...completed];
    setScopedTasks(visible);
    setTasks(visible.filter((task) => task.issuerId === session.userId
      || (task.status !== "DRAFT" && task.assigneeIds.includes(session.userId))
      || (task.status === "REQUESTED" && !task.collaborationSourceDepartmentId)));
  }, [session.userId]);

  const reloadTasks = useCallback(async () => {
    const [active, completed] = await Promise.all([api.visibleTasks(), api.completedTasks()]);
    setCompletedNextCursor(completed.nextCursor || null);
    applyVisibleTasks(active, completed.items);
  }, [applyVisibleTasks]);

  const reloadNotes = useCallback(async () => {
    const [nextNotes, nextRequests] = await Promise.all([api.researchNotes(), api.researchNoteDraftRequests()]);
    setNotes(nextNotes);
    setDraftRequests(nextRequests);
  }, []);

  const applyActiveTasks = useCallback((visible: Task[]) => {
    const merge = (current: Task[]) => [...visible, ...current.filter(task => task.status === "COMPLETED" && !visible.some(next => next.id === task.id))];
    setScopedTasks(merge);
    setTasks(current => merge(current).filter(task => task.issuerId === session.userId
      || (task.status !== "DRAFT" && task.assigneeIds.includes(session.userId))
      || (task.status === "REQUESTED" && !task.collaborationSourceDepartmentId)));
  }, [session.userId]);

  const reloadNotifications = useCallback(async () => {
    // Notification history can outlive a decision (or be disabled). Refresh actual pending work on the same timer.
    await Promise.all([
      api.notifications().then(setNotifications),
      api.visibleTasks().then(applyActiveTasks),
    ]);
  }, [applyActiveTasks]);

  const reload = useCallback(async () => {
    setLoading(true); setError("");
    let currentSettings: TenantSettings | null = null;
    try { currentSettings = await api.tenantSettings(); setTenantSettings(currentSettings); }
    catch (reason) { setError(reason instanceof Error ? reason.message : i18n.t("workspace.settingsError")); }
    const results = await Promise.allSettled([
      api.visibleTasks(),
      currentSettings?.modules.includes("PROJECTS") ? api.projects() : Promise.resolve([] as Project[]),
      api.departments(),
      api.users(),
      currentSettings?.modules.includes("RESEARCH_NOTES") ? api.researchNotes() : Promise.resolve([] as ResearchNote[]),
      currentSettings?.modules.includes("RESEARCH_NOTES") ? api.researchNoteDraftRequests() : Promise.resolve([] as ResearchNoteDraftRequest[]),
      api.notifications(),
      currentSettings?.modules.includes("ATTENDANCE") ? api.attendanceStatus() : Promise.resolve(null),
      api.myAdditionalPermissions(),
      api.myPreferences(),
      api.completedTasks(),
    ] as const);
    const visible = results[0].status === "fulfilled" ? results[0].value : [];
    const nextProjects = results[1].status === "fulfilled" ? results[1].value : [];
    const nextDepartments = results[2].status === "fulfilled" ? results[2].value : [];
    const nextUsers = results[3].status === "fulfilled" ? results[3].value : [];
    if (results[1].status === "fulfilled") setProjects(nextProjects);
    if (results[2].status === "fulfilled") setDepartments(nextDepartments);
    if (results[3].status === "fulfilled") setUsers(nextUsers);
    if (results[4].status === "fulfilled") setNotes(results[4].value);
    if (results[5].status === "fulfilled") setDraftRequests(results[5].value);
    if (results[6].status === "fulfilled") setNotifications(results[6].value);
    if (results[7].status === "fulfilled") setAttendance(results[7].value);
    if (results[8].status === "fulfilled") setAdditionalPermissions(results[8].value);
    if (results[9].status === "fulfilled") {
      setPersonalPreferences(results[9].value);
      applyPreferences(results[9].value);
    }
    const completed = results[10].status === "fulfilled" ? results[10].value : { items: [], nextCursor: null };
    setCompletedNextCursor(completed.nextCursor || null);
    applyVisibleTasks(visible, completed.items);
    const failed = results.find((result) => result.status === "rejected");
    if (failed?.status === "rejected") setError(failed.reason instanceof Error ? failed.reason.message : i18n.t("workspace.partialError"));
    loadedWorkspace.current = true;
    setLoading(false);
  }, [applyVisibleTasks]);
  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void reloadNotifications().catch(() => undefined);
    };
    const receivePush = (event: MessageEvent) => {
      if (event.data?.type === "ANGELFLOW_NOTIFICATION_RECEIVED") refreshWhenVisible();
    };
    const timer = window.setInterval(refreshWhenVisible, 15_000);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    navigator.serviceWorker?.addEventListener("message", receivePush);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      navigator.serviceWorker?.removeEventListener("message", receivePush);
    };
  }, [reloadNotifications]);
  useEffect(() => {
    void Promise.all([
      api.notificationPreferences(),
      api.webPushConfiguration(),
      currentWebPushSubscription(),
    ]).then(async ([preferences, configuration, subscription]) => {
      if (!preferences.webPushEnabled || !configuration.available || !subscription) return;
      const publicKey = resolveWebPushPublicKey(configuration.publicKey);
      if (publicKey) await api.saveWebPushSubscription(await subscribeCurrentBrowser(publicKey));
    }).catch(() => undefined);
  }, []);
  const hasActiveDraft = draftRequests.some((request) => request.status === "QUEUED" || request.status === "GENERATING");
  useEffect(() => {
    if (!hasActiveDraft) return;
    const timer = window.setInterval(() => {
      void api.researchNoteDraftRequests().then(async (requests) => {
        setDraftRequests(requests);
        if (!requests.some((request) => request.status === "QUEUED" || request.status === "GENERATING")) {
          setNotes(await api.researchNotes());
        }
      }).catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [hasActiveDraft]);
  const hasActiveTaskAiDraft = tasks.some((task) => ["QUEUED", "GENERATING", "CANCELLING"].includes(task.aiDraftStatus || ""));
  useEffect(() => {
    // 작성 창은 해당 업무 하나를 직접 폴링한다. 창이 열려 있을 때 목록 폴링까지 겹치지 않는다.
    if (!hasActiveTaskAiDraft || modal?.kind === "task") return;
    const timer = window.setInterval(() => {
      void api.visibleTasks().then(applyActiveTasks).catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [hasActiveTaskAiDraft, modal?.kind, applyActiveTasks]);
  useEffect(() => { if (!toast) return; const id = window.setTimeout(() => setToast(""), 2800); return () => clearTimeout(id); }, [toast]);

  const currentUser = users.find((user) => user.id === session.userId);
  // JWT 역할은 로그인 시점의 스냅샷이다. 조직에서 역할을 내린 경우 최신 사용자 정보를
  // 우선하고, 아직 확인하지 못했으면 권한 없음으로 본다. 서버도 현재 DB 역할을 확인한다.
  const effectiveSession: Session = { ...session, roles: currentUser?.roles ?? [] };
  const broadScope = effectiveSession.roles.includes("OWNER") || effectiveSession.roles.includes("MANAGER");
  const unread = notifications.filter((item) => !item.readAt).length;
  const modules = tenantSettings?.modules || ["TASKS"];
  const isOwner = effectiveSession.roles.includes("OWNER");
  const hasPermission = (permission: AdditionalPermission) => isOwner || additionalPermissions.includes(permission);
  const hasBudgetAccess = hasPermission("PROJECT_BUDGET_VIEW") || hasPermission("PROJECT_BUDGET_EDIT")
    || projects.some((project) => project.members.some((member) => member.userId === session.userId && member.role === "PM"));
  // 모든 구성원이 자신의 업무를 만들 수 있다. 실제 할당 범위는 작성 화면과 서버가 제한한다.
  const canCreateTask = true;
  const canCreateProject = broadScope;
  const deptScopeAvailable = broadScope || scopedTasks.length > tasks.length;
  const mineTasks = tasks.filter((task) => !task.collaborationSourceDepartmentId);
  const listedTasks = scopedTasks.filter((task) => !task.collaborationSourceDepartmentId);
  const collaborationTasks = scopedTasks.filter((task) => task.collaborationSourceDepartmentId);
  const needsApproval = (task: Task) => taskNeedsApproval(task, currentUser, projects.find(project => project.id === task.projectId), departments);
  const approvalDots = { tasks: listedTasks.some(needsApproval), collaboration: collaborationTasks.some(needsApproval) };
  const canConvertNote = (task: Task) => Boolean(tenantSettings && canConvertTaskToNote(task, effectiveSession, users.find((user) => user.id === session.userId), departments, tenantSettings.researchNoteConversionRole));
  const view = route.view;
  const selectedTask = route.view === "tasks" ? route.taskId : undefined;
  const selectedProject = route.view === "projects" ? route.projectId : undefined;
  const selectedNote = route.view === "notes" ? route.noteId : undefined;
  const settingsSection: SettingsSection = route.view === "settings" ? route.section : "system";
  const go = (next: WorkspaceRoute, replace = false) => {
    window.history[replace ? "replaceState" : "pushState"](null, "", workspaceRouteUrl(next));
    setRoute(next);
    setMobileNav(false);
  };
  useEffect(() => {
    const key = nav.find((item) => item.id === view)?.labelKey || (view === "personal-settings" ? "personal.title" : "nav.dashboard");
    const label = t(key);
    document.title = `${label} · AngelFlow`;
  }, [t, view]);
  useEffect(() => {
    if (loading) return;
    if (route.view === "settings" && route.section === "logs" && !isOwner) {
      go({ view: "settings", section: "system" }, true);
      return;
    }
    if (route.view === "settings" && route.section === "attendance" && !modules.includes("ATTENDANCE")) {
      go({ view: "settings", section: "system" }, true);
      return;
    }
    if (route.view === "projects" && route.page === "budget" && !hasBudgetAccess) {
      go({ view: "projects" }, true);
      return;
    }
    const item = nav.find((candidate) => candidate.id === route.view);
    if (!item || ((!item.module || modules.includes(item.module))
      && (!item.managerOnly || broadScope)
      && (!item.ownerOnly || isOwner)
      && (!item.nonOwnerOnly || !isOwner)
      && (!item.permission || hasPermission(item.permission)))) return;
    go({ view: "dashboard" }, true);
  }, [additionalPermissions, broadScope, hasBudgetAccess, isOwner, loading, modules, route]);
  const navigate = (next: View) => { setTaskProjectFilter(undefined); go(routeForView(next)); };
  const openTask = (id: string) => {
    const task = scopedTasks.find((item) => item.id === id);
    go({ view: "tasks", taskId: id });
    if (task?.status === "DRAFT" && task.issuerId === session.userId) {
      setModal({ kind: "task", task });
    }
  };
  useEffect(() => {
    const syncRoute = () => { setModal(null); setRoute(parseWorkspaceRoute()); setMobileNav(false); };
    window.addEventListener("popstate", syncRoute);
    return () => window.removeEventListener("popstate", syncRoute);
  }, []);
  useEffect(() => {
    const canonical = workspaceRouteUrl(route);
    if (`${window.location.pathname}${window.location.search}` !== canonical) {
      const googleCallback = /^#google_(state|error)=/.test(window.location.hash) ? window.location.hash : "";
      window.history.replaceState(null, "", canonical + googleCallback);
    }
  }, [route]);
  useEffect(() => {
    if (loading || route.view !== "tasks" || !route.taskId) return;
    const task = scopedTasks.find((item) => item.id === route.taskId);
    if (task?.status === "DRAFT" && task.issuerId === session.userId) {
      setModal((current) => current?.kind === "task" && current.task?.id === task.id ? current : { kind: "task", task });
    }
  }, [loading, route, scopedTasks, session.userId]);
  const notify = async (message: string) => { await reload(); setToast(message); };
  const notifyTasks = async (message: string) => { await reloadTasks(); setToast(message); };
  const notifyNotes = async (message: string) => { await reloadNotes(); setToast(message); };
  const renderWorkTask = (id: string, refreshWork: () => void) => <TaskDetail key={id} id={id} embedded
    session={effectiveSession} users={users} departments={departments} projects={projects} notes={notes} draftRequests={draftRequests}
    statusEnabled={modules.includes("STATUS")} onBack={() => undefined} onOpenTask={openTask}
    onOpenNote={noteId => go({ view: "notes", noteId })} onCreateTask={seed => setModal({ kind: "task", seed })}
    onEditTask={task => setModal({ kind: "task", task })} canConvertNote={canConvertNote} onCreateNote={task => setModal({ kind: "note", task })}
    onChanged={message => { refreshWork(); void notifyTasks(message); }} />;

  return <div className="shell">
    <button className={`nav-backdrop ${mobileNav ? "show" : ""}`} onClick={() => setMobileNav(false)} aria-label={t("header.closeMenu")} />
    <aside className={`sidebar ${mobileNav ? "open" : ""} ${navCollapsed ? "collapsed" : ""}`}>
      <WorkspaceSwitcher session={session} settings={tenantSettings} onMobileClose={() => setMobileNav(false)} />
      <nav>{["nav.dashboardGroup", "nav.execution", "nav.people", "nav.records", "nav.management", "nav.organization"].map((group) => {
        const items = nav.filter((item) => item.groupKey === group
          && (!item.module || modules.includes(item.module))
          && (!item.managerOnly || broadScope)
          && (!item.ownerOnly || isOwner)
          && (!item.nonOwnerOnly || !isOwner)
          && (!item.permission || hasPermission(item.permission)));
        if (!items.length) return null;
        return <div className="nav-group" key={group || "etc"}>
          {group && <p className="nav-group-label">{t(group)}</p>}
          {items.map((item) => {
            const Icon = item.icon;
            const label = t(item.labelKey);
            return <button className={view === item.id ? "active" : ""} aria-current={view === item.id ? "page" : undefined} onClick={() => navigate(item.id)} key={item.id} title={label}>
              <Icon size={16} /><span className="nav-label">{label}</span>
              {(item.id === "tasks" || item.id === "collaboration") && approvalDots[item.id]
                && <span className="nav-approval-dot" role="img" aria-label={t("nav.approvalPending")} title={t("nav.approvalPending")} />}
            </button>;
          })}
        </div>;
      })}</nav>
      <div className="sidebar-foot">
        <span className="nav-label"><BrandLogo className="app-brand-logo" /></span>
        <button onClick={() => setNavCollapsed(!navCollapsed)} title={t(navCollapsed ? "header.expandSidebar" : "header.collapseSidebar")} aria-label={t(navCollapsed ? "header.expandSidebar" : "header.collapseSidebar")}>{navCollapsed ? <ChevronsRight size={15} /> : <ChevronsLeft size={15} />}</button>
      </div>
    </aside>

    <div className="content-shell">
      <Header
        user={currentUser} email={session.email} unread={unread} notifications={notifications}
        attendance={modules.includes("ATTENDANCE") ? attendance : null}
        onAttendance={async (working) => {
          const next = working ? await api.clockOut() : await api.clockIn();
          setAttendance(next);
          setToast(t(working ? "attendance.clockedOut" : "attendance.clockedIn"));
        }}
        onMenu={() => setMobileNav(true)} onLogout={onLogout}
        personalSettingsOpen={view === "personal-settings"}
        onPersonalSettings={() => navigate("personal-settings")}
        onInquiry={() => setInquiryOpen(true)}
        onRead={async (item) => {
          await api.readNotification(item.id);
          setNotifications((all) => all.map((entry) => entry.id === item.id ? { ...entry, readAt: new Date().toISOString() } : entry));
          if (item.resourceType === "task") {
            openTask(item.resourceId);
          } else if (item.resourceType === "workflow_work_item") {
            try {
              const workItem = await api.workItem(item.resourceId);
              if (workItem.projectId) go({ view: "projects", projectId: workItem.projectId, page: "board" });
            } catch (reason) {
              setError(reason instanceof Error ? reason.message : "업무를 불러오지 못했습니다.");
            }
          } else if (item.resourceType === "status_dashboard") go({ view: "status", dashboardId: item.resourceId });
          else if (item.resourceType === "research_note") go({ view: "notes", noteId: item.resourceId });
          else if (item.resourceType === "leave_request") go({ view: "leave", section: item.eventType === "LEAVE_REQUESTED" ? "approvals" : undefined });
          else if (item.resourceType === "attendance_correction") {
            go(item.eventType === "ATTENDANCE_CORRECTION_REQUESTED" && hasPermission("ATTENDANCE_MANAGE")
              ? { view: "attendance-management", requestId: item.resourceId } : { view: "my-work" });
          }
        }}
        onReadAll={async () => {
          await api.readAllNotifications();
          const readAt = new Date().toISOString();
          setNotifications((all) => all.map((entry) => entry.readAt ? entry : { ...entry, readAt }));
        }}
      />
      {error && <div className="global-error">{error}<button onClick={() => void reload()}>{t("common.retry")}</button></div>}
      {loading && loadedWorkspace.current && <p className="sr-only" role="status">{t("workspace.loading")}</p>}
      {loading && !loadedWorkspace.current ? <main className="page"><Loading page layout={loadingLayoutForRoute(route)} label={t("workspace.loading")} /></main>
        : selectedTask ? <TaskDetail key={`${selectedTask}:${scopedTasks.find((task) => task.id === selectedTask)?.status || ""}`}
          id={selectedTask} session={effectiveSession} users={users} departments={departments} projects={projects} notes={notes} draftRequests={draftRequests} statusEnabled={modules.includes("STATUS")}
          backLabel={t(route.view === "tasks" && route.page === "completed" ? "tasks.completedTitle" : scopedTasks.find((task) => task.id === selectedTask)?.collaborationSourceDepartmentId ? "nav.collaboration" : "nav.tasks")}
          onBack={() => go(route.view === "tasks" && route.page === "completed" ? { view: "tasks", page: "completed" } : scopedTasks.find((task) => task.id === selectedTask)?.collaborationSourceDepartmentId ? { view: "collaboration" } : { view: "tasks" })} onOpenNote={(id) => go({ view: "notes", noteId: id })}
          onOpenTask={openTask} onCreateTask={(seed) => setModal({ kind: "task", seed })}
          onEditTask={(task) => setModal({ kind: "task", task })}
          canConvertNote={canConvertNote} onCreateNote={(task) => setModal({ kind: "note", task })} onChanged={(message) => void notifyTasks(message)}
        />
        : selectedProject ? <ProjectDetail
          id={selectedProject} session={effectiveSession} users={users} departments={departments} notes={notes}
          onChanged={(project) => setProjects((current) => current.map((item) => item.id === project.id ? project : item))}
          onBack={() => go({ view: "projects" })} onTask={openTask} onCreateTask={(seed) => setModal({ kind: "task", seed })}
          onOpenNote={(noteId) => go({ view: "notes", noteId })}
          onBudget={() => go({ view: "projects", page: "budget" })}
          page={route.view === "projects" && route.page !== "budget" ? route.page : undefined}
          documentId={route.view === "projects" ? route.documentId : undefined}
          workId={route.view === "projects" ? route.workId : undefined} renderTask={renderWorkTask}
          onOpenWork={workId => go({ view: "projects", projectId: selectedProject, page: "board", workId })}
          intake={route.view === "projects" ? route.intake : undefined}
          onOpenIntake={intake => go({ view: "projects", projectId: selectedProject, page: "board", intake })}
          onPage={(page, documentId) => go({ view: "projects", projectId: selectedProject, page, documentId })}
        />
        : selectedNote ? <NoteDetail id={selectedNote} session={effectiveSession} users={users} tasks={scopedTasks} projects={projects} onBack={() => go({ view: "notes" })} onOpenTask={openTask} onChanged={(message) => void notifyNotes(message)} />
        : <main className="page">
          {view === "dashboard" && <DashboardPage session={effectiveSession} tasks={mineTasks} boardEnabled={modules.includes("BOARD")} onOpenTask={openTask} onNavigate={navigate} />}
          {view === "calendar" && <CalendarPage key={`${effectiveSession.workspaceDomain}:${effectiveSession.userId}`} session={effectiveSession} users={users} departments={departments} />}
          {view === "boards" && modules.includes("BOARD") && <BoardsPage viewerId={session.userId} users={users} onChanged={notify} />}
          {view === "status" && modules.includes("STATUS") && <StatusPage tasks={scopedTasks} departments={departments} projects={projects} users={users} session={effectiveSession} canManage={broadScope} dashboardId={route.view === "status" ? route.dashboardId : undefined}
            workId={route.view === "status" ? route.workId : undefined} onOpenWork={workId => go({ view: "status", dashboardId: route.view === "status" ? route.dashboardId : undefined, workId })} renderTask={renderWorkTask}
            intake={route.view === "status" ? route.intake : undefined} onOpenIntake={intake => go({ view: "status", dashboardId: route.view === "status" ? route.dashboardId : undefined, intake })}
            onSelect={dashboardId => go({ view: "status", dashboardId })} onOpenTask={openTask} onChanged={notifyTasks} />}
          {view === "tasks" && (route.view === "tasks" && route.page === "completed"
            ? <CompletedTasksPage projects={projects} users={users} departments={departments} onBack={() => go({ view: "tasks" })} onOpen={(id) => go({ view: "tasks", page: "completed", taskId: id })} />
            : <TasksPage tasks={listedTasks} mineTaskIds={mineTasks.map((task) => task.id)} projects={projects} users={users} departments={departments} viewerId={session.userId} proposalOnly={!broadScope} scopeAvailable={deptScopeAvailable} canCreate={canCreateTask} initialProject={taskProjectFilter} hasMoreCompleted={Boolean(completedNextCursor)} onMoreCompleted={() => go({ view: "tasks", page: "completed" })} onOpen={openTask} onCreate={() => setModal({ kind: "task" })} onCopy={(task) => setModal({ kind: "task", seed: { copyFrom: task } })} />)}
          {view === "collaboration" && <CollaborationPage collaborationPublishPolicy={tenantSettings?.collaborationPublishPolicy} allowPastTaskDates={tenantSettings?.allowPastTaskDates ?? true} session={effectiveSession} tasks={collaborationTasks} departments={departments} users={users} projects={projects} onOpenTask={openTask} onTaskPersisted={(task) => {
            setTasks((current) => [task, ...current.filter((item) => item.id !== task.id)]);
            setScopedTasks((current) => [task, ...current.filter((item) => item.id !== task.id)]);
          }} onChanged={notifyTasks} />}
          {view === "projects" && (route.view === "projects" && route.page === "budget" && hasBudgetAccess
            ? <ProjectBudgetPage session={effectiveSession} permissions={additionalPermissions} projects={projects} onBack={() => go({ view: "projects" })} />
            : <ProjectsPage session={effectiveSession} permissions={additionalPermissions} projects={projects} tasks={scopedTasks} canCreate={canCreateProject} onOpen={(id) => go({ view: "projects", projectId: id })} onCreate={() => setModal({ kind: "project" })} onBudget={() => go({ view: "projects", page: "budget" })} />)}
          {view === "performance" && <PerformancePage session={effectiveSession} users={users} tasks={scopedTasks} />}
          {view === "evaluation" && <EvaluationPage session={effectiveSession} users={users} departments={departments} projects={projects} tasks={scopedTasks} />}
          {view === "notes" && <NotesPage canCreate={Boolean(tenantSettings && (tenantSettings.researchNoteConversionRole === "ASSIGNEE" || effectiveSession.roles.some((role) => role === "OWNER" || role === "MANAGER")))} notes={notes} draftRequests={draftRequests} tasks={scopedTasks} projects={projects} onOpen={(id) => go({ view: "notes", noteId: id })} onCreate={() => setModal({ kind: "note" })} onRetry={async (id) => { await api.retryResearchNoteDraft(id); await notifyNotes(t("workspace.noteRetry")); }} />}
          {view === "task-forms" && broadScope && <TaskFormsPage session={effectiveSession} onChanged={notify} />}
          {view === "settings" && isOwner && <SettingsPage session={effectiveSession} permissions={additionalPermissions} section={settingsSection} settings={tenantSettings} departments={departments} users={users} onSection={(section) => go({ view: "settings", section })} onChanged={notify} />}
          {view === "account-management" && hasPermission("USER_ACCOUNT_MANAGE") && !isOwner && <SettingsPage accountOnly session={effectiveSession} permissions={additionalPermissions} section="org" settings={tenantSettings} departments={departments} users={users} onSection={() => undefined} onChanged={notify} />}
          {view === "announcement-management" && hasPermission("BOARD_MANAGE") && <NoticeManagementPage users={users} departments={departments} onChanged={notify} />}
          {view === "company-rule-management" && hasPermission("COMPANY_RULE_MANAGE") && <CompanyRulesPage management onBack={() => go({ view: "org-chart", page: "rules" })} />}
          {view === "work-guides" && <WorkGuidesPage key="read" />}
          {view === "work-guide-management" && hasPermission("WORK_GUIDE_MANAGE") && <WorkGuidesPage key="manage" management />}
          {view === "my-work" && <MyWorkPage />}
          {view === "org-chart" && (route.view === "org-chart" && route.page === "rules"
            ? <CompanyRulesPage onBack={() => go({ view: "org-chart" })} />
            : <OrganizationChartPage session={effectiveSession} users={users} departments={departments} onRules={() => go({ view: "org-chart", page: "rules" })} />)}
          {route.view === "leave" && <LeavePage route={route} />}
          {route.view === "attendance-management" && hasPermission("ATTENDANCE_MANAGE") && <AttendanceManagementPage departments={departments} requestId={route.requestId} />}
          {view === "leave-management" && hasPermission("ATTENDANCE_MANAGE") && <LeaveManagementPage users={users} />}
          {view === "personal-settings" && <PersonalSettingsPage
            email={session.email}
            profile={currentUser}
            section={route.view === "personal-settings" ? route.section : "general"}
            initialPreferences={personalPreferences}
            onSection={(section) => go({ view: "personal-settings", section })}
            onPreferencesChanged={(preferences) => { setPersonalPreferences(preferences); applyPreferences(preferences); }}
            onPasswordChanged={(token) => onSessionChanged(markPasswordChanged(session, token))}
            onChanged={notify}
          />}
        </main>}
    </div>

    {modal?.kind === "task" && <TaskCompose collaborationPublishPolicy={tenantSettings?.collaborationPublishPolicy} session={effectiveSession} seed={modal.seed} task={modal.task} departments={departments} users={users} projects={projects} allowPastTaskDates={tenantSettings?.allowPastTaskDates ?? true} onClose={() => {
      if (modal.task && route.view === "tasks" && route.taskId === modal.task.id) go({ view: "tasks" });
      setModal(null);
    }} onDeleted={(id) => {
      setTasks((current) => current.filter((task) => task.id !== id));
      setScopedTasks((current) => current.filter((task) => task.id !== id));
      setToast("초안을 삭제했습니다.");
    }} onPersisted={(task) => {
      setTasks((current) => [task, ...current.filter((item) => item.id !== task.id)]);
      setScopedTasks((current) => [task, ...current.filter((item) => item.id !== task.id)]);
    }} onCreated={async (task, message) => {
      setTasks((current) => [task, ...current.filter((item) => item.id !== task.id)]);
      setScopedTasks((current) => [task, ...current.filter((item) => item.id !== task.id)]);
      setModal(null); go(task.status === "DRAFT" ? { view: "tasks" } : { view: "tasks", taskId: task.id }); setToast(message);
    }} />}
    {modal?.kind === "project" && <ProjectCreate users={users} departments={departments} onClose={() => setModal(null)} onCreated={async (project) => { setModal(null); await reload(); go({ view: "projects", projectId: project.id }); setToast(t("workspace.projectCreated")); }} />}
    {modal?.kind === "note" && <ResearchNoteCreateModal task={modal.task} canConvertNote={canConvertNote} tasks={scopedTasks} onClose={() => setModal(null)} onCreated={async () => { setModal(null); await notifyNotes(t("workspace.noteRequested")); }} />}
    {inquiryOpen && <SupportInquiryModal onClose={() => setInquiryOpen(false)} onCreated={() => setToast(t("support.created"))} />}
    {toast && <div className="toast"><CheckCircle2 size={16} />{toast}</div>}
  </div>;
}

function WorkspaceSwitcher({ session, settings, onMobileClose }: { session: Session; settings: TenantSettings | null; onMobileClose: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState<AccountWorkspace[] | null>(null);
  const [switching, setSwitching] = useState("");
  const [error, setError] = useState("");
  const displayName = settings?.displayName || session.workspaceName;

  async function toggle() {
    const next = !open;
    setOpen(next); setError("");
    if (next && !workspaces) {
      try { setWorkspaces(await api.accountWorkspaces()); }
      catch (reason) { setError(reason instanceof Error ? reason.message : t("workspace.switcherLoadError")); }
    }
  }

  async function switchTo(workspace: AccountWorkspace) {
    if (workspace.domain === session.workspaceDomain) { setOpen(false); return; }
    setSwitching(workspace.tenantId); setError("");
    try {
      const handoff = await api.createWorkspaceHandoff(workspace.tenantId);
      window.location.assign(workspaceHandoffUrl(handoff.workspace.domain, handoff.code));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t("workspace.switchError"));
      setSwitching("");
    }
  }

  return <div className="tenant">
    <button className="tenant-trigger" onClick={() => void toggle()} aria-expanded={open} aria-label={t("workspace.switcherLabel", { name: displayName })}>
      <span className="tenant-mark" title={displayName}>{settings?.logoUploaded
        ? <img src={api.publicTenantLogoUrl(session, settings.logoVersion) || api.workspaceLogoUrl(session.workspaceDomain)} alt={t("workspace.logo", { name: displayName })} />
        : <span className="tenant-initial">{initials(displayName).slice(0, 1)}</span>}
        <Building2 className="tenant-collapsed-icon" size={15} aria-hidden="true" />
      </span>
      <span className="nav-label"><b>{displayName}</b><small>{session.workspaceDomain}</small></span>
      <ChevronDown className="nav-label tenant-chevron" size={14} />
    </button>
    <button className="mobile-close" onClick={onMobileClose} aria-label={t("header.closeMenu")}><X size={17} /></button>
    {open && <div className="workspace-switcher-popover">
      <header><b>{t("workspace.memberships")}</b><small>{session.email}</small></header>
      <div>{workspaces ? workspaces.map((workspace) => <button key={workspace.tenantId} onClick={() => void switchTo(workspace)} disabled={Boolean(switching)}>
        {workspace.logoUploaded ? <img src={api.workspaceLogoUrl(workspace.domain)} alt="" /> : <span>{initials(workspace.displayName).slice(0, 2)}</span>}
        <i><b>{workspace.displayName}</b><small>{workspace.domain}</small></i>
        {switching === workspace.tenantId ? <LoaderCircle className="spin" size={14} /> : workspace.domain === session.workspaceDomain ? <Check size={14} /> : null}
      </button>) : <p><span className="context-spinner" /> {t("common.loading")}</p>}</div>
      {error && <footer>{error}</footer>}
    </div>}
  </div>;
}

function Header({ user, email, unread, notifications, attendance, personalSettingsOpen, onMenu, onRead, onReadAll, onPersonalSettings, onInquiry, onAttendance, onLogout }: {
  user?: User; email: string; unread: number; notifications: Notification[];
  attendance: AttendanceStatus | null;
  personalSettingsOpen: boolean;
  onMenu: () => void; onRead: (item: Notification) => void; onReadAll: () => Promise<void>; onPersonalSettings: () => void; onInquiry: () => void;
  onAttendance: (working: boolean) => Promise<void>; onLogout: () => void;
}) {
  const { t } = useTranslation();
  const [bell, setBell] = useState(false);
  const [readingAll, setReadingAll] = useState(false);
  const [help, setHelp] = useState(false);
  const bellRoot = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!bell) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!bellRoot.current?.contains(event.target as Node)) setBell(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    return () => document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, [bell]);
  async function readAll() {
    setReadingAll(true);
    try { await onReadAll(); }
    finally { setReadingAll(false); }
  }
  return <header className="topbar">
    <button className="menu-button" onClick={onMenu} aria-label={t("header.menu")}><Menu size={19} /></button>
    <div className="top-spacer" />
    {attendance && <AttendanceControl status={attendance} onAction={onAttendance} />}
    <div className="viewer"><Avatar name={user?.displayName || email} userId={user?.id} avatarVersion={user?.avatarVersion} /><span><b>{user?.displayName || email.split("@")[0]}</b><small>{user?.jobTitle || t("header.member")}</small></span></div>
    <span className="top-divider" />
    <button className={`icon-button ${personalSettingsOpen ? "active" : ""}`} onClick={onPersonalSettings} aria-label={t("personal.title")} title={t("personal.title")}><SettingsIcon size={17} /></button>
    <div className="help-wrap">
      <button className="icon-button" onClick={() => { setHelp(!help); setBell(false); }} aria-label={t("support.help")} title={t("support.help")} aria-expanded={help}><CircleHelp size={18} /></button>
      {help && <div className="help-popover" role="menu">
        <a href={publicSiteUrl("/guide")} target="_blank" rel="noreferrer" role="menuitem" onClick={() => setHelp(false)}><BookOpen size={16} /><span>{t("support.guide")}</span><ExternalLink size={13} aria-label={t("support.external")} /></a>
        <button type="button" role="menuitem" onClick={() => { setHelp(false); onInquiry(); }}><MessageCircle size={16} /><span>{t("support.title")}</span></button>
      </div>}
    </div>
    <div className="bell-wrap" ref={bellRoot}>
      <button className="icon-button" onClick={() => { setBell(!bell); setHelp(false); }} aria-label={t("personal.notifications")}><Bell size={17} />{unread > 0 && <em>{unread}</em>}</button>
      {bell && <div className="notification-popover">
        <header><b>{t("personal.notifications")}</b><div><span>{t("header.unread", { count: unread })}</span><button type="button" disabled={!unread || readingAll} onClick={() => void readAll()}>{t(readingAll ? "header.readingAll" : "header.readAll")}</button></div></header>
        <div>{notifications.length ? notifications.map((item) => <button className={!item.readAt ? "unread" : ""} key={item.id} onClick={() => { onRead(item); setBell(false); }}>
          <i /><span><b>{item.title}</b><small>{item.body}</small><time className="mono" dateTime={item.createdAt}>{koTime(item.createdAt)}</time></span>
        </button>) : <p className="empty-small">{t("header.noNotifications")}</p>}</div>
      </div>}
    </div>
    <button className="icon-button" onClick={onLogout} aria-label={t("header.logout")}><LogOut size={17} /></button>
  </header>;
}

function AttendanceControl({ status, onAction }: { status: AttendanceStatus; onAction: (working: boolean) => Promise<void> }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(status.actualWorkedSeconds);
  useEffect(() => {
    const syncedAt = Date.now();
    setElapsed(status.actualWorkedSeconds);
    if (!status.working) return;
    const timer = window.setInterval(() => {
      setElapsed(status.actualWorkedSeconds + Math.max(0, Math.floor((Date.now() - syncedAt) / 1000)));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [status]);
  const hours = Math.floor(elapsed / 3600).toString().padStart(2, "0");
  const minutes = Math.floor((elapsed % 3600) / 60).toString().padStart(2, "0");
  const seconds = Math.floor(elapsed % 60).toString().padStart(2, "0");
  async function act() {
    setBusy(true); setError("");
    try { await onAction(status.working); }
    catch (reason) { setError(reason instanceof Error ? reason.message : t("attendance.saveError")); }
    finally { setBusy(false); }
  }
  const time = `${hours}:${minutes}:${seconds}`;
  return <div className="attendance-control" title={error || (status.working ? t("attendance.currentTime", { time }) : t("attendance.startHint"))}>
    <span><Clock3 size={14} /><em>{t("attendance.work")}</em><b className="mono">{time}</b></span>
    <button type="button" className={status.working ? "clock-out" : "clock-in"} disabled={busy} onClick={() => void act()}>
      {busy ? t("attendance.processing") : t(status.working ? "attendance.clockOut" : "attendance.clockIn")}
    </button>
  </div>;
}

export { LayoutList as TasksIcon };
